// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import type {KeybindCommand, KeyCombo} from '@app/features/input/state/InputKeybind';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {KeybindHint} from '@app/features/ui/keybind_hint/KeybindHint';
import styles from '@app/features/voice_scene/components/VoiceSceneShortcutCard.module.css';
import {useFocusBackOnClose} from '@app/features/voice_scene/hooks/useFocusBackOnClose';
import RoomShortcutGuide from '@app/features/voice_scene/state/RoomShortcutGuide';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {KeyboardIcon, XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {type CSSProperties, useEffect, useRef} from 'react';

// The first-visit intro closes on its own after this long.
export const ROOM_INTRO_MS = 10_000;

const CARD_TITLE_DESCRIPTOR = msg({
	message: 'Room shortcuts',
	comment: 'Voice call 3D room: title of the card listing every keyboard shortcut of the room (opened with ?).',
});
const CLOSE_DESCRIPTOR = msg({
	message: 'Close',
	context: 'voice-scene-shortcuts',
	comment: 'Voice call 3D room: accessible name of the button that closes the shortcut card or the intro.',
});
const INTRO_TITLE_DESCRIPTOR = msg({
	message: 'Quick keys',
	comment: 'Voice call 3D room: title of the short intro shown once on the first visit, with the main keys.',
});
const INTRO_MORE_DESCRIPTOR = msg({
	message: 'All shortcuts',
	comment: 'Voice call 3D room: last item of the first-visit intro, next to the ? key; ? opens every shortcut.',
});
const GROUP_MOVE = msg({
	message: 'Moving',
	context: 'voice-scene-shortcuts',
	comment: 'Voice call 3D room shortcut card: heading over the walking keys.',
});
const GROUP_VIEW = msg({
	message: 'Camera',
	context: 'voice-scene-shortcuts',
	comment: 'Voice call 3D room shortcut card: heading over the camera keys.',
});
const GROUP_ACT = msg({
	message: 'Movements',
	context: 'voice-scene-shortcuts',
	comment: 'Voice call 3D room shortcut card: heading over the character movement keys.',
});
const GROUP_ROOM = msg({
	message: 'Room',
	context: 'voice-scene-shortcuts',
	comment: 'Voice call 3D room shortcut card: heading over the outfit and help keys.',
});
const WALK = msg({message: 'Walk', context: 'voice-scene-shortcuts', comment: 'Shortcut card: W A S D walk.'});
const RUN = msg({message: 'Run', context: 'voice-scene-shortcuts', comment: 'Shortcut card: Shift with W A S D runs.'});
const SIT = msg({
	message: 'Sit down / stand up',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: E sits on the seat in reach or stands up (at a table too).',
});
const LOOK = msg({
	message: 'Look around',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: dragging with the mouse turns the view.',
});
const DRAG = msg({
	message: 'Drag',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: the keycap that stands for dragging with the mouse. Keep it short.',
});
const PAN = msg({
	message: 'Move the view',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card, walking off: W A S D or the arrows slide the camera over the room.',
});
const TURN = msg({
	message: 'Turn the view',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card, walking off: Q and E turn the camera around the room.',
});
const EYES = msg({
	message: "My character's eyes",
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: the key that looks out through your own character’s eyes (first person).',
});
const FOLLOW = msg({
	message: 'Follow my character',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: the key that puts the camera behind your walking character.',
});
const HOME = msg({
	message: 'Starting view',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: H glides the camera back to the starting view of the whole room.',
});
const BACK = msg({
	message: 'Back / free the cursor',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: Esc goes back to the room view and lets go of the mouse while walking.',
});
const WAVE = msg({message: 'Wave', context: 'voice-scene-shortcuts', comment: 'Shortcut card: 1 waves.'});
const BODY = msg({
	message: 'Dance, jump, crouch, punch, faint',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: keys 2 to 6, in this order. Keep the order of the movement names.',
});
const POINT = msg({message: 'Point', context: 'voice-scene-shortcuts', comment: 'Shortcut card: B points.'});
const SLAP = msg({message: 'Slap', context: 'voice-scene-shortcuts', comment: 'Shortcut card: T slaps.'});
const ALL_MOVES = msg({
	message: 'Every movement',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: G opens every character movement.',
});
const OUTFIT = msg({
	message: 'Outfit',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: O opens the outfit.',
});
const THIS_CARD = msg({
	message: 'This card',
	context: 'voice-scene-shortcuts',
	comment: 'Shortcut card: ? opens and closes this card.',
});
const MOVES_INTRO = msg({
	message: 'Movements',
	context: 'voice-scene-intro',
	comment: 'First-visit intro: next to the keys 1–6.',
});

type Keys = ReadonlyArray<string | MessageDescriptor | {combo: KeyCombo} | {action: KeybindCommand}>;
interface Line {
	keys: Keys;
	label: MessageDescriptor;
}

// Fluxer's own keycaps (KeybindHint): the user's binding of an action (V, F) and the named keys, translated and
// shown the platform's way (Cmd on a Mac).
const SHIFT: {combo: KeyCombo} = {combo: {key: 'Shift'}};
const ESC: {combo: KeyCombo} = {combo: {key: 'Escape'}};
const EYES_KEY: {action: KeybindCommand} = {action: 'misc_toggle_room_eyes'};
const FOLLOW_KEY: {action: KeybindCommand} = {action: 'misc_toggle_room_follow'};

function shortcutGroups(walk: boolean): ReadonlyArray<{title: MessageDescriptor; lines: ReadonlyArray<Line>}> {
	return [
		{
			title: GROUP_MOVE,
			lines: walk
				? [
						{keys: ['W', 'A', 'S', 'D'], label: WALK},
						{keys: [SHIFT, '+', 'W'], label: RUN},
						{keys: ['E'], label: SIT},
						{keys: [DRAG], label: LOOK},
					]
				: [
						{keys: ['W', 'A', 'S', 'D'], label: PAN},
						{keys: ['Q', 'E'], label: TURN},
						{keys: [DRAG], label: LOOK},
					],
		},
		{
			title: GROUP_VIEW,
			lines: [
				{keys: [EYES_KEY], label: EYES},
				...(walk ? [{keys: [FOLLOW_KEY], label: FOLLOW}] : []),
				{keys: ['H'], label: HOME},
				{keys: [ESC], label: BACK},
			],
		},
		{
			title: GROUP_ACT,
			lines: [
				{keys: ['1'], label: WAVE},
				{keys: ['2', '–', '6'], label: BODY},
				{keys: ['B'], label: POINT},
				{keys: ['T'], label: SLAP},
				{keys: ['G'], label: ALL_MOVES},
			],
		},
		{
			title: GROUP_ROOM,
			lines: [
				{keys: ['O'], label: OUTFIT},
				{keys: ['?'], label: THIS_CARD},
			],
		},
	];
}

function KeyRun({keys}: {keys: Keys}) {
	const {i18n} = useLingui();
	return (
		<span className={styles.keys} data-flx="voice-scene.voice-scene-shortcut-card.keys">
			{keys.map((key, index) =>
				key === '+' || key === '–' ? (
					<span key={index} className={styles.joiner} data-flx="voice-scene.voice-scene-shortcut-card.joiner">
						{key}
					</span>
				) : typeof key === 'object' && ('combo' in key || 'action' in key) ? (
					<KeybindHint key={index} {...key} data-flx="voice-scene.voice-scene-shortcut-card.keybind-hint" />
				) : (
					<kbd key={index} className={styles.key} data-flx="voice-scene.voice-scene-shortcut-card.key">
						{typeof key === 'string' ? key : i18n._(key)}
					</kbd>
				),
			)}
		</span>
	);
}

function CloseButton({onClick}: {onClick: () => void}) {
	const {i18n} = useLingui();
	return (
		<FocusRing offset={-2} data-flx="voice-scene.voice-scene-shortcut-card.close-focus-ring">
			<button
				type="button"
				className={styles.close}
				aria-label={i18n._(CLOSE_DESCRIPTOR)}
				onMouseDown={(event) => event.preventDefault()}
				onClick={onClick}
				data-flx="voice-scene.voice-scene-shortcut-card.close"
			>
				<XIcon weight="bold" className={styles.closeIcon} data-flx="voice-scene.voice-scene-shortcut-card.close-icon" />
			</button>
		</FocusRing>
	);
}

// The "?" card: every key of the room, grouped, on the user's own bindings. Not modal: the room keeps its keys (? and
// Esc close it from the room; Esc closes it from inside it too).
export const VoiceSceneShortcutCard = observer(function VoiceSceneShortcutCard({
	canvas = null,
}: {
	canvas?: HTMLCanvasElement | null;
}) {
	const {i18n} = useLingui();
	const ref = useRef<HTMLDivElement>(null);
	const open = RoomShortcutGuide.cardOpen;
	useFocusBackOnClose(open, ref, canvas);
	if (!open) return null;
	const {walk} = readVoiceRoomAssignment(ExperimentAssignments.response);
	return (
		<div
			ref={ref}
			className={styles.card}
			role="dialog"
			aria-label={i18n._(CARD_TITLE_DESCRIPTOR)}
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					RoomShortcutGuide.closeAll();
				}
			}}
			data-flx="voice-scene.voice-scene-shortcut-card.card"
		>
			<div className={styles.header} data-flx="voice-scene.voice-scene-shortcut-card.header">
				<KeyboardIcon
					weight="bold"
					className={styles.titleIcon}
					data-flx="voice-scene.voice-scene-shortcut-card.icon"
				/>
				<span className={styles.title} data-flx="voice-scene.voice-scene-shortcut-card.title">
					{i18n._(CARD_TITLE_DESCRIPTOR)}
				</span>
				<CloseButton onClick={RoomShortcutGuide.closeAll} />
			</div>
			<div className={styles.groups} data-flx="voice-scene.voice-scene-shortcut-card.groups">
				{shortcutGroups(walk).map((group, index) => (
					<section key={index} className={styles.group} data-flx="voice-scene.voice-scene-shortcut-card.group">
						<h3 className={styles.groupTitle} data-flx="voice-scene.voice-scene-shortcut-card.group-title">
							{i18n._(group.title)}
						</h3>
						{group.lines.map((line, lineIndex) => (
							<div key={lineIndex} className={styles.line} data-flx="voice-scene.voice-scene-shortcut-card.line">
								<span className={styles.lineLabel} data-flx="voice-scene.voice-scene-shortcut-card.line-label">
									{i18n._(line.label)}
								</span>
								<KeyRun keys={line.keys} />
							</div>
						))}
					</section>
				))}
			</div>
		</div>
	);
});

// Once per device, the first time a room shows characters: the five keys that matter most, for ten seconds or until
// closed. Opening the card counts as seen. A status region, so a screen reader reads it once.
export const VoiceSceneShortcutIntro = observer(function VoiceSceneShortcutIntro() {
	const {i18n} = useLingui();
	const {walk, avatars} = readVoiceRoomAssignment(ExperimentAssignments.response);
	const pending =
		RoomShortcutGuide.introPending &&
		!RoomShortcutGuide.cardOpen &&
		avatars &&
		!VoiceSceneSettings.classicView &&
		!Accessibility.hideKeyboardHints;
	useEffect(() => {
		if (!pending) return;
		const timer = window.setTimeout(RoomShortcutGuide.dismissIntro, ROOM_INTRO_MS);
		return () => window.clearTimeout(timer);
	}, [pending]);
	if (!pending) return null;
	const items: ReadonlyArray<Line> = [
		...(walk
			? [
					{keys: ['W', 'A', 'S', 'D'], label: WALK},
					{keys: ['E'], label: SIT},
				]
			: []),
		{keys: ['1', '–', '6'], label: MOVES_INTRO},
		{keys: ['T'], label: SLAP},
		{keys: [EYES_KEY], label: EYES},
		{keys: ['?'], label: INTRO_MORE_DESCRIPTOR},
	];
	return (
		<div
			className={styles.intro}
			role="status"
			style={{'--room-intro-ms': `${ROOM_INTRO_MS}ms`} as CSSProperties}
			data-flx="voice-scene.voice-scene-shortcut-intro.intro"
		>
			<span className={styles.introTitle} data-flx="voice-scene.voice-scene-shortcut-intro.title">
				<KeyboardIcon
					weight="bold"
					className={styles.titleIcon}
					data-flx="voice-scene.voice-scene-shortcut-intro.icon"
				/>
				{i18n._(INTRO_TITLE_DESCRIPTOR)}
			</span>
			<span className={styles.introItems} data-flx="voice-scene.voice-scene-shortcut-intro.items">
				{items.map((item, index) => (
					<span key={index} className={styles.introItem} data-flx="voice-scene.voice-scene-shortcut-intro.item">
						<KeyRun keys={item.keys} />
						{i18n._(item.label)}
					</span>
				))}
			</span>
			<CloseButton onClick={RoomShortcutGuide.dismissIntro} />
			<span className={styles.timer} aria-hidden data-flx="voice-scene.voice-scene-shortcut-intro.timer" />
		</div>
	);
});
